import toNumber from 'lodash/toNumber'
import { useContext, useMemo } from 'react'

import { Money } from '@Generated/graphql'
import { MetadataContext } from '@Globals/Metadata'
import { useTranslation } from '@Globals/Translations'

export enum GiftcardAmountOptions {
  SELECT_AMOUNT = 'SELECT_AMOUNT',
  CUSTOM_AMOUNT = 'CUSTOM_AMOUNT',
}

export interface GiftCardAmountOption {
  key: string
  value: string
}

// TODO: Remove once graphQL is finished
const TEMPORARY_GIFTCARD_STORE = {
  predefinedValues: [] as Array<Money>,
  valueMax: {
    currencyCode: '',
    value: 300000,
    format: '3000',
  },
  valueMin: {
    currencyCode: '',
    value: 2000,
    format: '20',
  },
}

interface GiftCardStoreProps {
  predefinedValues: Array<Money>
  valueMax: Money
  valueMin: Money
}

/**
 * The store-configured gift card amounts (predefined options + allowed range),
 * shared between the product page's amount drawer and the add-to-cart prompt.
 */
export const useGiftCardAmountOptions = () => {
  const { t } = useTranslation()
  const { store } = useContext(MetadataContext)
  const currencyFraction = store.config?.currencyFraction ?? 2
  const {
    predefinedValues,
    valueMax,
    valueMin,
  }: GiftCardStoreProps = store.config?.giftcards ?? TEMPORARY_GIFTCARD_STORE

  return useMemo(() => {
    const getPlainMoneyValue = (money: Money) => toNumber(`${money.value}`.slice(0, -currencyFraction))

    const amountOptions: GiftCardAmountOption[] = [
      ...(predefinedValues?.map(money => ({
        key: `${getPlainMoneyValue(money)}`,
        value: money.format!,
      })) ?? []),
      {
        key: GiftcardAmountOptions.CUSTOM_AMOUNT,
        value: t('giftcard:GIFT_CARD_OTHER_AMOUNT', 'Other amount'),
      },
    ]

    return {
      amountOptions,
      maxValue: getPlainMoneyValue(valueMax),
      minValue: getPlainMoneyValue(valueMin),
    }
  }, [currencyFraction, predefinedValues, t, valueMax, valueMin])
}
